import { useState, useEffect, useRef } from "react";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifTheme,
  MotifModalFooter,
  MotifButton,
  MotifFormField,
  MotifLabel,
  MotifFileUploader,
  MotifFileUploaderItem,
  MotifIcon,
  MotifErrorMessage,
  MotifToast,
} from "@ey-xd/motif-react";
import { IconoirCloudUpload } from "@ey-xd/motif-icon";
import "./ApproveSkuModal.scss";
import {
  CFO_APPROVAL,
  CFO_APPROVAL_UPLOAD_MESSAGE,
  SKU_APPROVE,
  SKU_APPROVE_COST_TYPE,
  SKU_AVAILABLE_FOR_PURCHASE_IN_COMMERCIAL_HUB_MESSAGE,
  SKU_COST_EFFECTIVE_DATE_NOTE,
  ALLOWED_EXTENSIONS,
  MAX_FILE_SIZE,
  UPDATE_INTERVAL,
  UPLOAD_DURATION,
} from "../../pages/BMOSkuApproval/Constants";
import { isFileNameContainsDangerousExtensions } from "../../utils/Validator";
import { EMPTY_FILE_MESSAGE, EMPTY_FILE_TITLE } from "../../Constant";

export default function ApproveSkuModal({
  show = true,
  setShow,
  btnLabel = "Approve SKU",
  approveHandler,
  apiErrorMessage = "",
  onClearApiError,
  selectedSkuName,
  selectedSku,
  requestType = "",
  isLoading = false,
  fileBackendError = false,
  setFileBackendError,
}: {
  show: boolean;
  setShow: (isVisible: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave?: (updatedComment: any) => void;
  selectedSku?: string;
  approveHandler?: (action: string, file: File) => void;
  btnLabel: string;
  apiErrorMessage?: string;
  onClearApiError?: () => void;
  selectedSkuName?: string;
  requestType?: string;
  isLoading?: boolean;
  selectedSKUData?: any;
  fileBackendError: boolean;
  setFileBackendError: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [uploadedFile, setUploadedFile] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const uploadIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCostType = requestType === "New SKU" ? false : true;
  const aditionalDesc = isCostType ? "a cost update request for" : "";
  const [errorMessage, setErrorMessage] = useState<{
    header: string;
    message: string;
  }>({ header: "", message: "" });
  const [isCFOapprovalFileRequired, setIsCFOApprovalFileRequired] =
    useState<boolean>(false);

  const resetSKUApprovalModalState = () => {
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }
    setErrorMessage({ header: "", message: "" });
    setUploadedFile([]);
    setFileError(null);
    setUploadProgress(0);
    setIsUploading(false);
    setIsCFOApprovalFileRequired(false);
    onClearApiError?.();
  };

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
      resetSKUApprovalModalState();
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  useEffect(() => {
    return () => {
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
      }
    };
  }, []);

  const approveSku = async () => {
    if (fileError === EMPTY_FILE_MESSAGE) {
      setErrorMessage({
        header: EMPTY_FILE_TITLE,
        message: EMPTY_FILE_MESSAGE,
      });
      return;
    }

    onClearApiError?.();
    setIsCFOApprovalFileRequired(false);
    setErrorMessage({ header: "", message: "" });
    if (uploadedFile.length === 0) {
      setIsCFOApprovalFileRequired(true);
      setErrorMessage({
        header: "CFO approval required",
        message:
          "You can’t approve this SKU without uploading the CFO’s approval document.",
      });
      return;
    }
    if (approveHandler) {
      approveHandler(
        isCostType ? SKU_APPROVE_COST_TYPE : SKU_APPROVE,
        uploadedFile[0],
      );
    } else {
      setShow(false);
      resetSKUApprovalModalState();
    }
  };

  const simulateFileUpload = (file: File) => {
    // Clear any existing interval
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadedFile([file]);

    const steps = UPLOAD_DURATION / UPDATE_INTERVAL;
    const incrementPerStep = 100 / steps;
    let currentProgress = 0;

    uploadIntervalRef.current = setInterval(() => {
      currentProgress += incrementPerStep;

      if (currentProgress >= 100) {
        setUploadProgress(100);
        setIsUploading(false);
        if (uploadIntervalRef.current) {
          clearInterval(uploadIntervalRef.current);
          uploadIntervalRef.current = null;
        }
      } else {
        setUploadProgress(Math.round(currentProgress));
      }
    }, UPDATE_INTERVAL);
  };

  const processFiles = (files: any) => {
    setIsCFOApprovalFileRequired(false);
    setErrorMessage({ header: "", message: "" });
    if (!files) return;

    // Handle direct File object
    if (files instanceof File) {
      uploadFile([files]);
      return;
    }

    // Handle FileList or array-like object
    if (files.length !== undefined && files.length > 0) {
      // Check if first element is a File
      if (files[0] instanceof File) {
        uploadFile(files);
      } else if (files[0]?.file instanceof File) {
        // Handle wrapped file objects
        uploadFile([files[0].file]);
      }
      return;
    }

    // Handle event object with dataTransfer or target.files
    if (files.dataTransfer?.files || files.target?.files) {
      uploadFile(files.dataTransfer?.files || files.target.files);
      return;
    }

    // Handle object with file property
    if (files.file instanceof File) {
      uploadFile([files.file]);
      return;
    }
  };

  const uploadFile = (files: FileList | File[]) => {
    if (!files?.length) return;
    setErrorMessage({ header: "", message: "" });
    setFileError(null);
    const file = files[0] as File;
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    // Validate file extension
    if (!fileExtension || !ALLOWED_EXTENSIONS.includes(fileExtension as any)) {
      setFileError(
        `The file ${file.name} is not a supported format. Please upload a PNG, PDF, DOC, DOCX and MSG.`,
      );
      setUploadedFile([]);
      return;
    }

    //validate double extension
    const isValidFile = isFileNameContainsDangerousExtensions(file.name);
    if (!isValidFile) {
      setErrorMessage({
        header: "Invalid file type",
        message: `Your file ${file.name} contains an unsupported extensions. Allowed file types: PNG, PDF, DOC, DOCX, MSG. Please remove any restricted extensions (e.g. PHP, ASP, EXE) and try again`,
      });
      setUploadedFile([]);
      return;
    }

    if (file.size === 0) {
      setUploadedFile([file]);
      setFileError(EMPTY_FILE_MESSAGE);
      setErrorMessage({
        header: EMPTY_FILE_TITLE,
        message: EMPTY_FILE_MESSAGE,
      });
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setFileError(
        `The size of your file ${file.name} is bigger than 15 MB. Please select and upload file according to the size limitations.`,
      );
      setUploadedFile([]);
      return;
    }

    setFileError(null);
    simulateFileUpload(file);
  };

  const handleFileRemove = () => {
    if (uploadIntervalRef.current) {
      clearInterval(uploadIntervalRef.current);
      uploadIntervalRef.current = null;
    }
    setUploadedFile([]);
    setFileError(null);
    setUploadProgress(0);
    setIsUploading(false);
    setFileBackendError(false);
  };

  const closeToastHandler = () => {
    setErrorMessage({ header: "", message: "" });
    onClearApiError?.();
    setFileBackendError(false);
  };

  const closeSKUApprovalModal = () => {
    resetSKUApprovalModalState();
    setShow(false);
    setFileBackendError(false);
  };
  const fileUploaderLabel = isCFOapprovalFileRequired
    ? "Please select a file for upload"
    : "Click to upload or drag and drop";

  return (
    <div className="approve-sku-modal">
      <MotifTheme variant={"light"}></MotifTheme>
      <MotifModal
        className="approveSkuModal"
        id="ApproveSkuModal"
        show={show}
        onClose={closeSKUApprovalModal}
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        <>
          <MotifModalHeader>
            {isCostType
              ? "Confirm cost update SKU approval"
              : "Confirm new SKU approval"}
          </MotifModalHeader>
          <MotifModalBody>
            <div className="content">
              You are about to approve {aditionalDesc}{" "}
              <span className="sku-name-bold">
                {selectedSkuName} – {selectedSku}
              </span>
              . <br />
              <span>
                {isCostType
                  ? CFO_APPROVAL_UPLOAD_MESSAGE
                  : `${CFO_APPROVAL_UPLOAD_MESSAGE} ${SKU_AVAILABLE_FOR_PURCHASE_IN_COMMERCIAL_HUB_MESSAGE}`}
              </span>
            </div>
            {isCostType && (
              <>
                <br />
                <span className="content">{SKU_COST_EFFECTIVE_DATE_NOTE}</span>
              </>
            )}
            {errorMessage.message !== "" && (
              <div className="error-message-div">
                <MotifToast
                  onClose={closeToastHandler}
                  variant="error"
                  supportingText={errorMessage.message}
                >
                  <div>{errorMessage.header}</div>
                </MotifToast>
              </div>
            )}
            {apiErrorMessage !== "" && (
              <div className="error-message-div">
                <MotifToast onClose={closeToastHandler} variant="error">
                  <div>{apiErrorMessage}</div>
                </MotifToast>
              </div>
            )}
            <div className="upload-section">
              <MotifFormField>
                <MotifLabel className="approval-label">
                  {CFO_APPROVAL}
                </MotifLabel>
                <>
                  <MotifFileUploader
                    key={
                      isCFOapprovalFileRequired
                        ? "upload-required"
                        : "upload-default"
                    }
                    label={fileUploaderLabel}
                    labelDescription="PNG, PDF, DOC, DOCX and MSG (max. 15MB)"
                    onDrop={processFiles}
                    onChange={processFiles}
                    maxFiles={1}
                    className={
                      `${uploadedFile.length > 0 ? "hide-uploader" : ""}` +
                      `${fileError || isCFOapprovalFileRequired ? " motif-invalid" : ""}`
                    }
                  >
                    {uploadedFile.length > 0 &&
                      uploadedFile.map((file) => (
                        <MotifFileUploaderItem
                          key={file.name}
                          aria-label="Item of uploaded file list"
                          fileIcon={<MotifIcon src={IconoirCloudUpload} />}
                          fileName={file.name}
                          fileSize={file.size}
                          percentUploaded={uploadProgress}
                          showProgress={
                            uploadProgress > 0 && uploadProgress < 100
                          }
                          uploaded={uploadProgress === 100}
                          uploadedText=""
                          removable={!isUploading}
                          onRemove={handleFileRemove}
                        />
                      ))}
                  </MotifFileUploader>
                  {fileError && fileError !== EMPTY_FILE_MESSAGE && (
                    <MotifErrorMessage>{fileError}</MotifErrorMessage>
                  )}
                  {fileBackendError && (
                    <div className="error-message-div">
                      <MotifToast
                        className="file-error-tost"
                        onClose={closeToastHandler}
                        variant="error"
                        supportingText={`Your file ${uploadedFile[0]?.name} contains an unsupported extensions. Allowed file types: PNG, PDF, DOC, DOCX, MSG. Please remove any restricted extensions (e.g. PHP, ASP, EXE) and try again`}
                      >
                        <div>{"Invalid file type"}</div>
                      </MotifToast>
                    </div>
                  )}
                </>
              </MotifFormField>
            </div>
          </MotifModalBody>
          <MotifModalFooter>
            <MotifButton
              variant={"secondary"}
              size="small"
              onClick={closeSKUApprovalModal}
            >
              Cancel
            </MotifButton>
            <MotifButton
              variant={"primary"}
              size="small"
              onClick={approveSku}
              disabled={isUploading}
              loading={isLoading}
            >
              {btnLabel}
            </MotifButton>
          </MotifModalFooter>
        </>
      </MotifModal>
    </div>
  );
}
